import { apiRequest, buildWcUrl } from './api';
import { Order, BillingAddress, ShippingAddress } from '../types/woocommerce';
import { useCartStore } from '../store/useCartStore';

export interface CreateOrderData {
  billing: BillingAddress;
  shipping: ShippingAddress;
  payment_method: string;
  payment_method_title: string;
  set_paid: boolean;
  line_items: Array<{
    product_id: number;
    variation_id?: number;
    quantity: number;
  }>;
  shipping_lines?: Array<{
    method_id: string;
    method_title: string;
    total: string;
  }>;
  customer_note?: string;
}

// Service pour les commandes
export const ordersService = {
  // Créer une nouvelle commande
  async createOrder(orderData: CreateOrderData): Promise<Order> {
    return apiRequest.post<Order>(buildWcUrl('orders'), orderData);
  },

  // Récupérer une commande par ID
  async getOrder(orderId: number): Promise<Order> {
    return apiRequest.get<Order>(buildWcUrl(`orders/${orderId}`));
  },

  // Mettre à jour une commande
  async updateOrder(orderId: number, updates: Partial<Order>): Promise<Order> {
    return apiRequest.put<Order>(buildWcUrl(`orders/${orderId}`), updates);
  },

  // Créer une commande depuis le panier
  async createOrderFromCart(
    billing: BillingAddress,
    shipping: ShippingAddress,
    paymentMethod: string,
    customerNote?: string
  ): Promise<Order> {
    const cart = useCartStore.getState();
    
    if (cart.items.length === 0) {
      throw new Error('Le panier est vide');
    }

    const lineItems = cart.items.map(item => ({
      product_id: item.product.id,
      variation_id: item.variationId,
      quantity: item.quantity,
    }));

    const orderData: CreateOrderData = {
      billing,
      shipping,
      payment_method: paymentMethod,
      payment_method_title: this.getPaymentMethodTitle(paymentMethod),
      set_paid: false,
      line_items: lineItems,
      customer_note: customerNote,
    };

    return this.createOrder(orderData);
  },

  // Obtenir le titre de la méthode de paiement
  getPaymentMethodTitle(method: string): string {
    const methods: Record<string, string> = {
      'bacs': 'Virement bancaire',
      'cheque': 'Chèque',
      'cod': 'Paiement à la livraison',
      'paypal': 'PayPal',
      'stripe': 'Carte de crédit',
    };
    return methods[method] || method;
  },

  // Calculer les frais de livraison (simplifiés)
  calculateShipping(subtotal: number): number {
    if (subtotal >= 50) {
      return 0; // Livraison gratuite au-dessus de 50€
    }
    return 5.99; // Frais fixes
  },

  // Valider les données de facturation
  validateBillingAddress(billing: Partial<BillingAddress>): string[] {
    const errors: string[] = [];

    if (!billing.first_name?.trim()) errors.push('Le prénom est requis');
    if (!billing.last_name?.trim()) errors.push('Le nom est requis');
    if (!billing.email?.trim()) errors.push('L\'email est requis');
    if (!billing.phone?.trim()) errors.push('Le téléphone est requis');
    if (!billing.address_1?.trim()) errors.push('L\'adresse est requise');
    if (!billing.city?.trim()) errors.push('La ville est requise');
    if (!billing.postcode?.trim()) errors.push('Le code postal est requis');
    if (!billing.country?.trim()) errors.push('Le pays est requis');

    // Validation email
    if (billing.email && !this.isValidEmail(billing.email)) {
      errors.push('L\'email n\'est pas valide');
    }

    return errors;
  },

  // Valider l'email
  isValidEmail(email: string): boolean {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  },

  // Formater le numéro de commande
  formatOrderNumber(orderNumber: string): string {
    return `#${orderNumber}`;
  },

  // Obtenir le statut en français
  getOrderStatusLabel(status: Order['status']): string {
    const statusLabels: Record<Order['status'], string> = {
      'pending': 'En attente',
      'processing': 'En cours de traitement',
      'on-hold': 'En attente de paiement',
      'completed': 'Terminée',
      'cancelled': 'Annulée',
      'refunded': 'Remboursée',
      'failed': 'Échouée',
    };
    return statusLabels[status] || status;
  },

  // Obtenir la couleur du statut
  getOrderStatusColor(status: Order['status']): string {
    const statusColors: Record<Order['status'], string> = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'processing': 'bg-blue-100 text-blue-800',
      'on-hold': 'bg-orange-100 text-orange-800',
      'completed': 'bg-green-100 text-green-800',
      'cancelled': 'bg-gray-100 text-gray-800',
      'refunded': 'bg-purple-100 text-purple-800',
      'failed': 'bg-red-100 text-red-800',
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  },
};

